"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Globe,
  Mail,
  Lock,
  Phone,
  User,
  ShieldCheck,
  CheckCircle2,
  Loader2,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  Zap,
  CreditCard,
  SendHorizontal,
  Copy,
  Smartphone,
  AlertCircle,
  ExternalLink,
} from "lucide-react";

interface BackendPlan {
  id: number;
  name: string;
  display_name: string;
  description: string;
  max_products: number;
  max_staff: number;
  max_orders_per_month: number;
  custom_domain: boolean;
  ai_recommendation?: boolean;
  ai_content_generator?: boolean;
  monthly_price: string | number;
  yearly_price: string | number;
  is_active: boolean;
}

export default function CreateStoreOnboardingPage() {
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);

  const [plans, setPlans] = useState<BackendPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<BackendPlan | null>(null);

  const rootDomain = process.env.NEXT_PUBLIC_ROOT_DOMAIN || "shop.xylotechsolution.com";
  const [domainSuffix, setDomainSuffix] = useState(`.${rootDomain}`);

  // পেমেন্ট মোড: "gateway" (SSLCommerz) অথবা "manual" (ম্যানুয়াল TrxID)
  const [paymentType, setPaymentType] = useState<"gateway" | "manual">("gateway");
  const [gatewayLoading, setGatewayLoading] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState<"sslcommerz" | "bkash" | "nagad" | "card">("sslcommerz");
  const [phoneNumber, setPaymentPhoneNumber] = useState("");
  const [trxId, setTrxId] = useState("");
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [copied, setCopied] = useState(false);

  const [storeName, setStoreName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const merchantNumber = "01700-000000";

  // পরিবেশ অনুযায়ী স্বয়ংক্রিয় API বেজ URL নির্ধারণ
  const getApiBaseUrl = () => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return "http://localhost:8000/api/v1";
      }
    }
    return "/api/v1";
  };

  // SSLCommerz থেকে পেমেন্ট সফল হয়ে ফিরলে স্বয়ংক্রিয়ভাবে ৩ নম্বর ধাপে যাওয়া
  useEffect(() => {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const statusParam = searchParams.get("status");
      const tranIdParam = searchParams.get("tran_id");

      if (statusParam === "success" && tranIdParam) {
        setTrxId(tranIdParam);
        setPaymentMethod("sslcommerz");

        const savedPlan = localStorage.getItem("onboarding_selected_plan");
        if (savedPlan) {
          try {
            setSelectedPlan(JSON.parse(savedPlan));
          } catch (e) {
            console.error("Plan parse error:", e);
          }
        }

        setCurrentStep(3);
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
  }, []);

  // পরিবেশ অনুযায়ী সাবডোমেন সাফিক্স আপডেট করা
  useEffect(() => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      const port = window.location.port;
      if (host.includes("localhost") || host === "127.0.0.1") {
        setDomainSuffix(port ? `.localhost:${port}` : ".localhost");
      } else {
        setDomainSuffix(`.${rootDomain}`);
      }
    }
  }, [rootDomain]);

  const getAdminDashboardUrl = (sub: string) => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      const port = window.location.port;
      const protocol = window.location.protocol;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return `${protocol}//${sub}.localhost${port ? `:${port}` : ""}/admin`;
      }
      return `${protocol}//${sub}.${rootDomain}/admin`;
    }
    return `https://${sub}.${rootDomain}/admin`;
  };

  // ব্যাকএন্ড থেকে প্ল্যান ফেচ করা
  useEffect(() => {
    async function fetchPlans() {
      setLoadingPlans(true);
      const apiBase = getApiBaseUrl();

      try {
        let res = await fetch(`${apiBase}/subscriptions/plans/`, {
          headers: { Accept: "application/json" },
        });

        if (!res.ok) {
          res = await fetch(`${apiBase}/plans/`, {
            headers: { Accept: "application/json" },
          });
        }

        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data.results || [];
          setPlans(list);
          if (list.length > 0 && !selectedPlan) {
            setSelectedPlan(list[0]);
          }
        }
      } catch (err) {
        console.error("Failed to load subscription plans:", err);
      } finally {
        setLoadingPlans(false);
      }
    }

    fetchPlans();
  }, [selectedPlan]);

  const handleCopyNumber = () => {
    navigator.clipboard.writeText(merchantNumber.replace(/-/g, ""));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStoreNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setStoreName(val);
    const slug = val
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .slice(0, 20);
    setSubdomain(slug);
  };

  // ১. SSLCommerz অনলাইন পেমেন্ট কল
  const handleGatewayPayment = async () => {
    if (!selectedPlan) return;
    setGatewayLoading(true);
    setErrorMsg("");

    try {
      if (typeof window !== "undefined") {
        localStorage.setItem("onboarding_selected_plan", JSON.stringify(selectedPlan));
      }

      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/payments/transactions/initiate-subscription/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_name: selectedPlan.display_name,
          amount: Number(selectedPlan.monthly_price),
          billing_cycle: "monthly",
          customer_name: storeName || "Merchant Owner",
          customer_email: email || "merchant@example.com",
          customer_phone: phoneNumber || "01700000000",
        }),
      });

      const data = await res.json();
      if (res.ok && data.gateway_url) {
        window.location.href = data.gateway_url;
      } else {
        setErrorMsg(data.error || "পেমেন্ট গেটওয়েতে সমস্যা হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।");
      }
    } catch {
      setErrorMsg("সার্ভারের সাথে সংযোগ স্থাপন সম্ভব হয়নি।");
    } finally {
      setGatewayLoading(false);
    }
  };

  // ২. পূর্বের ম্যানুয়াল TrxID ভ্যালিডেশন
  const handleManualPayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!trxId.trim()) {
      setErrorMsg("অনুগ্রহ করে একটি ভ্যালিড Transaction ID (TrxID) দিন।");
      return;
    }

    setErrorMsg("");
    setIsVerifyingPayment(true);
    setPaymentMethod("bkash");

    setTimeout(() => {
      setIsVerifyingPayment(false);
      setCurrentStep(3);
    }, 1000);
  };

  // ৩. ফাইনাল স্টোর রেজিস্ট্রেশন
  const handleCreateStore = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);
    const apiBase = getApiBaseUrl();

    try {
      const res = await fetch(`${apiBase}/tenants/register-store/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: selectedPlan?.id,
          plan_name: selectedPlan?.display_name,
          payment_method: paymentMethod,
          payment_trx_id: trxId.trim().toUpperCase(),
          store_name: storeName.trim(),
          subdomain: subdomain.trim().toLowerCase(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim(),
          password,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setCurrentStep(4);
      } else {
        setErrorMsg(
          data.error ||
          data.detail ||
          data.message ||
          "স্টোর রেজিস্ট্রেশন সম্পন্ন করা যায়নি। তথ্যগুলো পুনরায় যাচাই করুন।"
        );
      }
    } catch {
      setErrorMsg("সার্ভারের সাথে সংযোগ স্থাপন করা সম্ভব হয়নি।");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-100 flex flex-col justify-between font-sans selection:bg-amber-500/30 selection:text-amber-200">
      
      {/* 🌟 ১. মূল ওয়েবসাইটের মতো নেভবার */}
      <header className="border-b border-white/5 px-4 sm:px-10 py-3.5 sticky top-0 bg-[#07090E]/90 backdrop-blur-xl z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          {/* লোগো ও ব্র্যান্ড নাম */}
          <Link href="https://xylotechsolution.com" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-black/60 border border-white/10 group-hover:border-amber-500/50 flex items-center justify-center p-2 transition-all shadow-lg shadow-black/60">
              <Image
                src="/logo.png"
                alt="Xylo Tech Solutions"
                width={32}
                height={32}
                className="w-full h-full object-contain"
                priority
              />
            </div>
            <div className="flex flex-col">
              <span className="font-black text-sm tracking-wider text-white uppercase group-hover:text-amber-400 transition-colors">
                Xylo Tech Solutions
              </span>
              <span className="text-[10px] text-amber-500/90 font-mono tracking-widest font-semibold uppercase">
                SaaS & AI Ecosystem
              </span>
            </div>
          </Link>

          {/* নেভ লিংকস (মূল সাইটের মতো) */}
          <nav className="hidden lg:flex items-center gap-7 text-[11px] font-bold tracking-widest uppercase text-slate-400">
            <a href="https://xylotechsolution.com" className="hover:text-white transition-colors">
              HOME
            </a>
            <a href="https://xylotechsolution.com/#services" className="hover:text-white transition-colors">
              SERVICES
            </a>
            <a href="https://xylotechsolution.com/#works" className="hover:text-white transition-colors">
              WORKS
            </a>
            <a href="https://xylotechsolution.com/#blogs" className="hover:text-white transition-colors">
              BLOGS
            </a>
            <a href="https://xylotechsolution.com/#contact" className="hover:text-white transition-colors">
              CONTACT
            </a>
          </nav>

          {/* মার্চেন্ট লগইন বাটন */}
          <div className="flex items-center gap-3">
            <Link
              href="/login"
              className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-500/40 text-xs font-bold text-slate-200 hover:text-white transition-all flex items-center gap-1.5 shadow-sm active:scale-95"
            >
              <span>মার্চেন্ট লগইন</span>
              <ArrowRight className="w-3.5 h-3.5 text-amber-400" />
            </Link>
          </div>
        </div>
      </header>

      {/* 🌟 ২. মাল্টি-স্টেপ প্রোগ্রেস বার (গোল্ডেন থিম) */}
      {currentStep !== 4 && (
        <div className="max-w-xl mx-auto w-full px-6 pt-10">
          <div className="flex items-center justify-between relative">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 w-full bg-white/10 -z-0" />

            <div className="flex flex-col items-center gap-1.5 relative z-10">
              <div
                className={`w-9 h-9 rounded-2xl flex items-center justify-center text-xs font-black transition-all ${
                  currentStep >= 1
                    ? "bg-gradient-to-tr from-amber-500 to-yellow-400 text-black shadow-lg shadow-amber-500/25 ring-4 ring-[#07090E]"
                    : "bg-slate-900 border border-white/10 text-slate-500"
                }`}
              >
                1
              </div>
              <span className={`text-[11px] font-bold ${currentStep >= 1 ? "text-amber-400" : "text-slate-500"}`}>
                প্ল্যান নির্বাচন
              </span>
            </div>

            <div className="flex flex-col items-center gap-1.5 relative z-10">
              <div
                className={`w-9 h-9 rounded-2xl flex items-center justify-center text-xs font-black transition-all ${
                  currentStep >= 2
                    ? "bg-gradient-to-tr from-amber-500 to-yellow-400 text-black shadow-lg shadow-amber-500/25 ring-4 ring-[#07090E]"
                    : "bg-slate-900 border border-white/10 text-slate-500"
                }`}
              >
                2
              </div>
              <span className={`text-[11px] font-bold ${currentStep >= 2 ? "text-amber-400" : "text-slate-500"}`}>
                পেমেন্ট
              </span>
            </div>

            <div className="flex flex-col items-center gap-1.5 relative z-10">
              <div
                className={`w-9 h-9 rounded-2xl flex items-center justify-center text-xs font-black transition-all ${
                  currentStep === 3
                    ? "bg-gradient-to-tr from-amber-500 to-yellow-400 text-black shadow-lg shadow-amber-500/25 ring-4 ring-[#07090E]"
                    : "bg-slate-900 border border-white/10 text-slate-500"
                }`}
              >
                3
              </div>
              <span className={`text-[11px] font-bold ${currentStep === 3 ? "text-amber-400" : "text-slate-500"}`}>
                স্টোর সেটআপ
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 🌟 ৩. মূল কন্টেন্ট সেকশন */}
      <main className="max-w-6xl w-full mx-auto px-6 py-10">
        {errorMsg && (
          <div className="max-w-xl mx-auto mb-6 p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs text-center font-medium flex items-center justify-center gap-2 shadow-lg">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* ধাপ ১: প্ল্যান নির্বাচন */}
        {currentStep === 1 && (
          <div className="space-y-10">
            <div className="text-center space-y-3 max-w-2xl mx-auto">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 text-amber-400 text-xs font-bold border border-amber-500/20 shadow-inner">
                <Sparkles className="w-3.5 h-3.5" /> [01] SOFTWARE • SAAS • AI SOLUTIONS
              </span>
              <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-tight">
                আপনার ব্যবসার জন্য সঠিক প্ল্যান বেছে নিন
              </h1>
              <p className="text-xs sm:text-sm text-slate-400 max-w-lg mx-auto leading-relaxed">
                প্যাকেজ অনুযায়ী পেমেন্ট সম্পন্ন করে তাৎক্ষণিকভাবে আপনার ডেডিকেটেড ক্লাউড স্টোর তৈরি করুন।
              </p>
            </div>

            {loadingPlans ? (
              <div className="py-24 flex flex-col items-center justify-center gap-3 text-slate-400 text-xs">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                <span>লোড হচ্ছে ডাটাবেজের সাবস্ক্রিপশন প্ল্যানসমূহ...</span>
              </div>
            ) : plans.length === 0 ? (
              <div className="text-center py-16 bg-[#0E1118] border border-white/10 rounded-3xl p-6 text-slate-400 text-xs max-w-md mx-auto space-y-2">
                <p>ডাটাবেজে কোনো সক্রিয় সাবস্ক্রিপশন প্ল্যান পাওয়া যায়নি।</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
                {plans.map((plan) => {
                  const isSelected = selectedPlan?.id === plan.id;
                  const price = Number(plan.monthly_price || 0);

                  return (
                    <div
                      key={plan.id}
                      className={`relative rounded-3xl p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 border ${
                        isSelected
                          ? "bg-[#0E121C] border-amber-500 shadow-2xl shadow-amber-500/10 ring-1 ring-amber-500/50"
                          : "bg-[#0A0D14]/80 border-white/10 hover:border-amber-500/30"
                      }`}
                    >
                      {plan.name === "pro" && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black text-[10px] font-black uppercase tracking-wider px-3.5 py-1 rounded-full shadow-md">
                          সবচেয়ে জনপ্রিয়
                        </span>
                      )}

                      <div className="space-y-5">
                        <div>
                          <h3 className="text-xl font-black text-white">{plan.display_name}</h3>
                          <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                            {plan.description || "অনলাইন ব্যবসার জন্য পরিপূর্ণ হাই-পারফরম্যান্স সলিউশন"}
                          </p>
                        </div>

                        <div className="flex items-baseline gap-1.5 border-y border-white/10 py-5">
                          <span className="text-4xl sm:text-5xl font-black text-white font-mono">
                            ৳{price.toLocaleString()}
                          </span>
                          <span className="text-xs text-slate-400 font-medium">/মাসিক</span>
                        </div>

                        <ul className="space-y-3 text-xs text-slate-300">
                          <li className="flex items-center gap-2.5">
                            <Check className="w-4 h-4 text-amber-400 shrink-0" />
                            <span>সর্বোচ্চ {plan.max_products}টি প্রোডাক্ট আপলোড</span>
                          </li>
                          <li className="flex items-center gap-2.5">
                            <Check className="w-4 h-4 text-amber-400 shrink-0" />
                            <span>{plan.max_staff} জন ডেডিকেটেড স্টাফ অ্যাকাউন্ট</span>
                          </li>
                          <li className="flex items-center gap-2.5">
                            <Check className="w-4 h-4 text-amber-400 shrink-0" />
                            <span>প্রতি মাসে {plan.max_orders_per_month}টি অর্ডার প্রসেসিং</span>
                          </li>
                          <li className="flex items-center gap-2.5">
                            <Check className="w-4 h-4 text-amber-400 shrink-0" />
                            <span>
                              {plan.custom_domain
                                ? "কাস্টম ডোমেন যুক্ত করার সুবিধা"
                                : `ফ্রি স্টোর সাবডোমেন (${domainSuffix})`}
                            </span>
                          </li>
                          <li className="flex items-center gap-2.5">
                            <Check className="w-4 h-4 text-amber-400 shrink-0" />
                            <span>AI RFM কাস্টমার সেগমেন্টেশন ও ফোরকাস্টিং</span>
                          </li>
                        </ul>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPlan(plan);
                          setCurrentStep(2);
                        }}
                        className={`w-full mt-8 py-3.5 rounded-full text-xs font-black transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 ${
                          isSelected
                            ? "bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black shadow-lg shadow-amber-500/25"
                            : "bg-white/10 hover:bg-white/15 text-white border border-white/10"
                        }`}
                      >
                        <span>এই প্ল্যানটি নির্বাচন করুন</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ধাপ ২: পেমেন্ট মেথড (SSLCommerz অনলাইন গেটওয়ে + ম্যানুয়াল TrxID) */}
        {currentStep === 2 && selectedPlan && (
          <div className="max-w-xl mx-auto">
            <div className="bg-[#0E121C] border border-white/10 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> প্ল্যান পরিবর্তন করুন
              </button>

              <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-amber-400 block">
                    নির্বাচিত প্যাকেজ
                  </span>
                  <h3 className="text-base font-black text-white">{selectedPlan.display_name}</h3>
                </div>
                <div className="text-right">
                  <span className="text-xl font-black text-white font-mono">
                    ৳{Number(selectedPlan.monthly_price).toLocaleString()}
                  </span>
                  <span className="text-[10px] text-slate-400 block">/মাসিক</span>
                </div>
              </div>

              {/* মেথড টগল ট্যাব */}
              <div className="grid grid-cols-2 gap-2 bg-black/60 p-1.5 rounded-2xl border border-white/10">
                <button
                  type="button"
                  onClick={() => { setPaymentType("gateway"); setErrorMsg(""); }}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    paymentType === "gateway"
                      ? "bg-gradient-to-r from-amber-500 to-yellow-500 text-black shadow-md shadow-amber-500/20 font-black"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  <span>অনলাইন গেটওয়ে</span>
                </button>

                <button
                  type="button"
                  onClick={() => { setPaymentType("manual"); setErrorMsg(""); }}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    paymentType === "manual"
                      ? "bg-gradient-to-r from-amber-500 to-yellow-500 text-black shadow-md shadow-amber-500/20 font-black"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  <SendHorizontal className="w-4 h-4" />
                  <span>ম্যানুয়াল সেন্ড মানি</span>
                </button>
              </div>

              {/* ১. SSLCommerz অনলাইন পেমেন্ট অপশন */}
              {paymentType === "gateway" && (
                <div className="space-y-5 animate-in fade-in duration-200">
                  <div className="p-4 rounded-2xl bg-black/50 border border-white/10 space-y-2">
                    <span className="text-xs font-bold text-amber-400 flex items-center gap-1.5">
                      <Zap className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span>তাৎক্ষণিক অটোমেটিক অনুমোদন</span>
                    </span>
                    <p className="text-[11px] text-slate-300 leading-relaxed">
                      SSLCommerz-এর মাধ্যমে বিকাশ, নগদ, রকেট, কার্ড কিংবা নেট ব্যাংকিং দিয়ে সুরক্ষিতভাবে পেমেন্ট সম্পন্ন করুন।
                    </p>
                    <div className="flex items-center gap-2 pt-1 text-[11px] text-slate-400 font-medium">
                      <Smartphone className="w-3.5 h-3.5 text-amber-400" />
                      <span>bKash, Nagad, Rocket, Visa, Mastercard সাপোর্টেড</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGatewayPayment}
                    disabled={gatewayLoading}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-black text-sm flex items-center justify-center gap-2 shadow-xl shadow-amber-500/20 transition-all cursor-pointer active:scale-95 disabled:opacity-50"
                  >
                    {gatewayLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-black" />
                    ) : (
                      <CreditCard className="w-5 h-5 text-black" />
                    )}
                    <span>SSLCommerz দিয়ে পেমেন্ট করুন</span>
                  </button>
                </div>
              )}

              {/* ২. ম্যানুয়াল TrxID অপশন */}
              {paymentType === "manual" && (
                <form onSubmit={handleManualPayment} className="space-y-4 text-xs animate-in fade-in duration-200">
                  <div className="p-3.5 rounded-2xl bg-black/60 border border-white/10 text-slate-400 flex items-center justify-between text-[11px]">
                    <div>
                      <p className="font-bold text-slate-300">মার্চেন্ট নম্বরে Send Money করুন:</p>
                      <p className="font-mono text-amber-400 font-bold text-xs mt-0.5">
                        {merchantNumber} <span className="text-[10px] text-slate-500 font-normal">(Personal/Merchant)</span>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleCopyNumber}
                      className="p-2 rounded-xl bg-white/10 hover:bg-white/15 text-slate-300 hover:text-white transition-all text-xs flex items-center gap-1 cursor-pointer"
                      title="নম্বর কপি করুন"
                    >
                      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copied ? "কপি হয়েছে" : "কপি"}</span>
                    </button>
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">যে নম্বর থেকে টাকা পাঠিয়েছেন</label>
                    <input
                      type="tel"
                      required
                      placeholder="017XXXXXXXX"
                      value={phoneNumber}
                      onChange={(e) => setPaymentPhoneNumber(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl bg-black/50 border border-white/10 text-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">Transaction ID (TrxID) *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. BL94A8K9Z"
                      value={trxId}
                      onChange={(e) => setTrxId(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl bg-black/50 border border-white/10 text-amber-400 font-mono font-bold uppercase focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isVerifyingPayment || !trxId.trim()}
                    className="w-full mt-2 py-3.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-black transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25 cursor-pointer disabled:opacity-50 active:scale-95"
                  >
                    {isVerifyingPayment ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-black" />
                        <span>পেমেন্ট যাচাই করা হচ্ছে...</span>
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 text-black" />
                        <span>পেমেন্ট নিশ্চিত করে দোকান খুলুন</span>
                      </>
                    )}
                  </button>
                </form>
              )}

              <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 pt-2 border-t border-white/10">
                <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                <span>১০০% নিরাপদ ও এনক্রিপ্টেড পেমেন্ট প্রসেসিং</span>
              </div>
            </div>
          </div>
        )}

        {/* ধাপ ৩: স্টোর সেটআপ */}
        {currentStep === 3 && selectedPlan && (
          <div className="max-w-xl mx-auto">
            <div className="bg-[#0E121C] border border-white/10 rounded-3xl p-8 space-y-6 shadow-2xl">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 text-xs font-bold border border-amber-500/20">
                  <CheckCircle2 className="w-3.5 h-3.5" /> পেমেন্ট ভেরিফাইড ({selectedPlan.display_name})
                </div>
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                  দোকানের বিবরণ ও অ্যাকাউন্ট সেটআপ
                </h1>
              </div>

              <form onSubmit={handleCreateStore} className="space-y-4 text-xs">
                <div className="space-y-1">
                  <label className="font-bold text-slate-300">দোকানের নাম *</label>
                  <input
                    type="text"
                    required
                    placeholder="যেমন: Nextlook"
                    value={storeName}
                    onChange={handleStoreNameChange}
                    className="w-full px-4 py-2.5 rounded-xl bg-black/50 border border-white/10 text-white focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-300">পছন্দের স্টোর সাবডোমেন *</label>
                  <div className="relative flex items-center">
                    <Globe className="w-4 h-4 absolute left-3.5 text-slate-500" />
                    <input
                      type="text"
                      required
                      placeholder="nextlook"
                      value={subdomain}
                      onChange={(e) =>
                        setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))
                      }
                      className="w-full pl-10 pr-48 py-2.5 rounded-xl bg-black/50 border border-white/10 text-amber-400 font-mono font-bold focus:outline-none focus:border-amber-500"
                    />
                    <span className="absolute right-3 text-slate-500 font-bold font-mono text-[11px]">
                      {domainSuffix}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">ফার্স্ট নেম *</label>
                    <input
                      type="text"
                      required
                      placeholder="Hasib"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/50 border border-white/10 text-white focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">লাস্ট নেম *</label>
                    <input
                      type="text"
                      required
                      placeholder="Sheikh"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/50 border border-white/10 text-white focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-300">মার্চেন্ট ইমেইল *</label>
                  <input
                    type="email"
                    required
                    placeholder="merchant@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-black/50 border border-white/10 text-white focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-300">ফোন নম্বর *</label>
                  <input
                    type="tel"
                    required
                    placeholder="017XXXXXXXX"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-black/50 border border-white/10 text-white focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-300">পাসওয়ার্ড *</label>
                  <input
                    type="password"
                    required
                    placeholder="নূন্যতম ৬ অক্ষর"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-black/50 border border-white/10 text-white focus:outline-none focus:border-amber-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-4 py-3.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-black transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25 cursor-pointer disabled:opacity-50 active:scale-95 text-xs"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-black" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 text-black" />
                  )}
                  <span>আবেদন জমা দিন</span>
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ধাপ ৪: সফল আবেদন */}
        {currentStep === 4 && (
          <div className="max-w-md mx-auto">
            <div className="bg-[#0E121C] border border-white/10 rounded-3xl p-8 text-center space-y-6 shadow-2xl">
              <div className="w-16 h-16 bg-amber-500/10 text-amber-400 rounded-2xl flex items-center justify-center mx-auto border border-amber-500/20">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <div className="space-y-2">
                <h2 className="text-2xl font-black text-white">আবেদন সফল হয়েছে!</h2>
                <p className="text-xs text-slate-400 leading-relaxed">
                  আপনার <strong className="text-amber-400">{selectedPlan?.display_name}</strong> সাবস্ক্রিপশন ও দোকান তৈরির আবেদন গৃহীত হয়েছে।
                </p>
              </div>

              <div className="pt-2">
                <Link
                  href={getAdminDashboardUrl(subdomain)}
                  className="w-full py-3.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black text-xs font-black transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25"
                >
                  <span>স্টোর অ্যাডমিন ড্যাশবোর্ডে যান</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* 🌟 ৪. ফুটার */}
      <footer className="border-t border-white/5 py-6 px-6 text-center text-xs text-slate-500 flex flex-col sm:flex-row items-center justify-between max-w-7xl mx-auto w-full gap-3">
        <div className="flex items-center gap-2">
          <Image src="/logo.png" alt="Xylo Logo" width={18} height={18} className="object-contain opacity-80" />
          <span>© 2026 Xylo Tech Solutions. All rights reserved.</span>
        </div>
        <div className="flex items-center gap-4 text-[11px]">
          <a href="https://xylotechsolution.com" className="hover:text-amber-400 transition-colors">
            Official Website
          </a>
          <span>•</span>
          <span className="text-slate-600">Enterprise Multi-Tenant SaaS Platform</span>
        </div>
      </footer>
    </div>
  );
}